import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { Spinner, Alert } from 'react-bootstrap';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// RSI calculation function
const calculateRSI = (prices, period = 14) => {
    if (prices.length <= period) return [];

    let gains = [];
    let losses = [];

    for (let i = 1; i < prices.length; i++) {
        const change = prices[i] - prices[i - 1];
        if (change > 0) {
            gains.push(change);
            losses.push(0);
        } else {
            gains.push(0);
            losses.push(Math.abs(change));
        }
    }

    const avgGain = gains.slice(0, period).reduce((a, b) => a + b, 0) / period;
    const avgLoss = losses.slice(0, period).reduce((a, b) => a + b, 0) / period;

    let rsi = [];
    let relativeStrength = avgLoss === 0 ? 100 : avgGain / avgLoss;
    rsi.push(100 - (100 / (1 + relativeStrength)));

    for (let i = period; i < gains.length; i++) {
        const currentGain = gains[i];
        const currentLoss = losses[i];
        const newAvgGain = (avgGain * (period - 1) + currentGain) / period;
        const newAvgLoss = (avgLoss * (period - 1) + currentLoss) / period;

        relativeStrength = newAvgLoss === 0 ? 100 : newAvgGain / newAvgLoss;
        rsi.push(100 - (100 / (1 + relativeStrength)));
    }

    return rsi;
};

const PriceChart = ({ tickerSymbol }) => {
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchChartData = async () => {
            if (!tickerSymbol) return;

            try {
                setLoading(true);
                setError(null);
                setChartData([]);

                // Fetch historical data (e.g., from a new endpoint)
                // This assumes an endpoint that returns daily prices for the last year
                const response = await axios.get(
                    `${API_BASE_URL}/api/stocks/historical/${tickerSymbol}`
                );

                const historicalData = response.data;

                if (historicalData && historicalData.length > 0) {
                    const prices = historicalData.map(d => d.close);
                    const rsiValues = calculateRSI(prices);

                    const data = historicalData.map((d, i) => ({
                        date: new Date(d.date).toLocaleDateString(),
                        price: d.close,
                        rsi: i >= 14 ? rsiValues[i - 14] : null,
                    }));
                    setChartData(data);
                }

                setLoading(false);
            } catch (err) {
                setError('Failed to fetch price chart data');
                setLoading(false);
            }
        };

        fetchChartData();
    }, [tickerSymbol]);

    if (loading) {
        return <div className="text-center p-5"><Spinner animation="border" /></div>;
    }

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    if (chartData.length === 0) {
        return <Alert variant="info">No price data available for this stock.</Alert>;
    }

    return (
        <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="price" yAxisId="left" fill="#8884d8" stroke="#8884d8" name="Price" />
                <Line type="monotone" dataKey="rsi" yAxisId="right" stroke="#82ca9d" name="RSI" dot={false} />
            </ComposedChart>
        </ResponsiveContainer>
    );
};

export default PriceChart;
